import Api from "shared/service/api/api";
import JwtService from "shared/service/jwt.service";

// actions
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN_BY_TOKEN = "loginByToken";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REGISTER = "register";
export const LOAD_USER = "loadUser";
export const SET_DEFAULT_PROJECT = "setDefaultProject";
export const SHADOW_LOGIN = "shadowLogin";
export const SHADOW_LOGOUT = "shadowLogout";

// mutations
export const PURGE_AUTH = "purgeAuth";
export const SET_AUTH = "setAuth";
export const SET_ERROR = "setError";
export const SET_USER = "setUser";
export const UPDATE_PROJECT_ID = "updateProjectId";
export const SET_PROJECT_ID = "setProjectId";
export const SET_BONUS_PACKAGES = "setBonusPackages";
export const SET_CURRENT_PROJECT_NAME = "setCurrentProjectName";
export const INCREMENT_AUTH_REFRESH_REQUEST_COUNT = "incrementAuthRefreshRequestCount";
export const DECREMENT_AUTH_REFRESH_REQUEST_COUNT = "decrementAuthRefreshRequestCount";

const state = {
  errors: null,
  user: {
    id: null,
    name: null,
    nick_name: null,
    avatar: null,
    projects: [
      {
        id: null,
        name: null,
        role: {
          id: null,
          name: null,
        },
        logo: {
          id: null,
          url: null,
        },
      },
    ],
    is_verified_phone: false,
    client_id: null,
    organization: null
  },
  projectId: null,
  accessToken: JwtService.getAccessToken(),
  expiresIn: parseInt(JwtService.getExpiresIn()),
  isAuthenticated: !!JwtService.getAccessToken(),
  bonusPackages: [],
  balance: 0,
  bonus_count: 0,

  authRefreshRequestCount: 0,
};

const getters = {
  currentUser: (state) => state.user,
  accessToken: (state) => state.accessToken,
  isAuthenticated: (state) => state.isAuthenticated,
  projects: (state) => Object.values(state.user.projects || {}),
  projectId: (state) => state.projectId,
  bonusPackages: (state) => state.bonusPackages,
  isRefreshingAuth: (state) => state.authRefreshRequestCount !== 0,
  showShadowLogoutButton: () => JwtService.getData("blg"),
};

const actions = {
  [LOGIN_BY_TOKEN]({ commit }, { token }) {
    return Api.auth.loginByToken(token).then((response) => {
      commit(
        response.success ? SET_AUTH : SET_ERROR,
        response.success ? response.data.token_group : response.data.errors
      );
      return response;
    });
  },
  [LOGIN]({ commit }, { email, password }) {
    return Api.auth.login(email, password).then((response) => {
      commit(
        response.success ? SET_AUTH : SET_ERROR,
        response.success ? response.data.token_group : response.data.errors
      );
      return response;
    });
  },
  [LOGOUT]({ commit }) {
    return Api.auth.logout().then((response) => {
      commit(PURGE_AUTH);
      return response;
    });
  },
  [REGISTER]({ commit }, { username, email, password, termsOfServiceAgreement, referralId }) {
    return Api.auth.registration(username, email, password, termsOfServiceAgreement, referralId).then((response) => {
      commit(
        response.success ? SET_AUTH : SET_ERROR,
        response.success ? response.data.token_group : response.data.errors
      );
      return response;
    });
  },
  async [VERIFY_AUTH]({ commit, state }) {
    if (!state.isAuthenticated) {
      commit(PURGE_AUTH);
      return false;
    }

    // выставляем время жизни токена на 2 минуты меньше чем есть на самом деле, чтобы он не сдох пока делается запрос
    const two_minutes = 2 * 60 * 1000;
    if (Date.now() < state.expiresIn - two_minutes) {
      return true;
    }

    const response = await Api.auth.refreshToken(JwtService.getRefreshToken());

    // the refresh token requset got cancelled
    if (response === undefined) {
      return true;
    }

    response.success ? commit(SET_AUTH, response.data) : commit(PURGE_AUTH);

    return response.success;
  },
  async [LOAD_USER]({ commit }) {
    const response = await Api.account.info();

    if (response.success) {
      commit(SET_USER, response.data);
      commit(UPDATE_PROJECT_ID);
      commit(SET_BONUS_PACKAGES, response.data.bonus_packages);
    }

    return response;
  },
  [SET_DEFAULT_PROJECT](_, projectId) {
    Api.account.setDefaultProject(projectId).then(() => {
      window.location.reload();
    });
  },

  [INCREMENT_AUTH_REFRESH_REQUEST_COUNT]({ commit }) {
    commit(INCREMENT_AUTH_REFRESH_REQUEST_COUNT);
  },
  [DECREMENT_AUTH_REFRESH_REQUEST_COUNT]({ commit }) {
    commit(DECREMENT_AUTH_REFRESH_REQUEST_COUNT);
  },

  [SHADOW_LOGIN]({ commit }, userId) {
    return Api.auth.shadowLogin(userId).then((response) => {
      if (!response.success) {
        throw new Error(response.error);
      }

      commit(SET_AUTH, response.data.token_group);
      commit(SET_PROJECT_ID, response.data.project_id);
    });
  },

  [SHADOW_LOGOUT]({ commit }) {
    return Api.auth.shadowLogout().then((response) => {
      if (!response.success) {
        throw new Error(response.error);
      }

      commit(SET_AUTH, response.data.token_group);
    });
  },
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_USER](state, user) {
    state.user = user;
  },
  [UPDATE_PROJECT_ID](state) {
    if (state.user.projects.length > 0) {
      state.projectId = state.user.projects[0].id;
    }
  },
  [SET_PROJECT_ID](state, projectId) {
    state.projectId = projectId;
  },
  [SET_AUTH](state, { access_token, refresh_token, expires_in }) {
    const expiresIn = expires_in * 1000;

    state.isAuthenticated = true;
    state.accessToken = access_token;
    state.expiresIn = expiresIn;

    state.errors = null;

    JwtService.saveAccessToken(access_token);
    JwtService.saveRefreshToken(refresh_token);
    JwtService.saveExpiresIn(expiresIn);
  },
  [SET_BONUS_PACKAGES](state, bonusPackages) {
    state.bonusPackages = bonusPackages;
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.user = {};
    state.accessToken = null;
    state.expiresIn = null;
    state.errors = null;

    JwtService.removeAccessToken();
    JwtService.removeRefreshToken();
    JwtService.removeExpiresIn();
  },
  [SET_CURRENT_PROJECT_NAME](state, name) {
    state.user.projects[0].name = name;
  },

  [INCREMENT_AUTH_REFRESH_REQUEST_COUNT](state) {
    ++state.authRefreshRequestCount;
  },
  [DECREMENT_AUTH_REFRESH_REQUEST_COUNT](state) {
    --state.authRefreshRequestCount;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
